import { Paths, PathValue } from "@mctypes/global.types";
import { Button, Radio, Paper, Stack, Title } from "@mantine/core";
import { HSpace, McInputNew, McTextAreaNew } from "../../../../../../../components";
import {
  IGKLekkasjeskanningAvHepaResultater,
  IGKRapport,
} from "@mcapps/shared/reportSchemas/gkReportSchema";
import ReportDocxImageContainer from "../../../../ReportBoard/ReportDocxImageContainer";
import { beregnetMaksimalLekkasjeverdi } from "../../gkGlobalUtils";
const LekkasjeskanningAvHepa: React.FC<{
  onUpdateEditData: <P extends Paths<IGKRapport, 4>>(path: P, value: PathValue<IGKRapport, P>) => void;
  data: IGKLekkasjeskanningAvHepaResultater;
}> = ({ data, onUpdateEditData }) => {
  const addNewRow = () => {
    const newFilter = {
      filterNr: "",
      maaleresultaterOppstroems: null,
      godkjent: null,
      maaleresultaterNedstroems: null,
      maksimalLekkasjeverdi: null,
      beskrivelseAvLekkasjeLokasjon: "",
      konsentrasjonFoerFilterEtterTest: null,
    };
    const updatedMaaleresultater = [...data.inndata.maaleresultater, newFilter];
    onUpdateEditData("testResultater.lekkasjeskanningAvHepa.inndata.maaleresultater", updatedMaaleresultater);
    return updatedMaaleresultater;
  };
  const removeLastRow = () => {
    const updatedMaaleresultater = [...data.inndata.maaleresultater];
    if (updatedMaaleresultater.length > 0) {
      updatedMaaleresultater.pop(); // Remove the last element
      onUpdateEditData("testResultater.lekkasjeskanningAvHepa.inndata.maaleresultater", updatedMaaleresultater);
    }
  };
  const updateMaaleresultater = (resultat: string | number | boolean, i: number, parameter: string) => {
    const maaleresultater = [...data.inndata.maaleresultater];
    maaleresultater[i][parameter] = resultat;
    maaleresultater[i].maksimalLekkasjeverdi = beregnetMaksimalLekkasjeverdi(
      maaleresultater[i].maaleresultaterOppstroems,
    );
    onUpdateEditData("testResultater.lekkasjeskanningAvHepa.inndata.maaleresultater", maaleresultater);
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      <Stack gap="xs">
        <Title order={4} fw={400}>Lekkasjeskanning av HEPA-filter</Title>
        <div className="d-flex flex-column">
          <b>Hvor mange målinger skal utføres? &#40;-&#41;:</b>
          <div className="d-flex">
            <Button color="green" onClick={addNewRow}>
              Legg til rad
            </Button>
            <HSpace />
            <Button variant="danger-filled" onClick={removeLastRow}>
              Fjern siste rad
            </Button>
          </div>
        </div>
        {Number(data.inndata.maaleresultater.length) > 0 && (
          <div className="mt-4 d-flex flex-column">
            <b>Fyll inn måleresultater:</b>
            <table>
              <thead>
                <tr>
                  <th>Filternr.</th>
                  <th>
                    Resultat oppstrøms &#40;mg/m<sup>3</sup>&#41;
                  </th>
                  <th className="d-flex justify-content-center">Verdi &gt; enn 0,01 &#40;%&#41;?</th>
                  <th>Belastning etter test &#40;%&#41;</th>
                  {data.inndata.maaleresultater.some((resultat) => resultat.godkjent === true) && (
                    <>
                      <th>Resultat nedstrøms &#40;%&#41;</th>
                      <th>Lekkasjelokasjon</th>
                    </>
                  )}
                </tr>
              </thead>
              {data.inndata.maaleresultater.map((filter, i) => (
                <tbody key={i}>
                  <tr>
                    <td>
                      <McInputNew
                        value={filter.filterNr}
                        type="text"
                        onChange={(e) => updateMaaleresultater(e.target.value, i, "filterNr")}
                      />
                    </td>
                    <td>
                      <McInputNew
                        value={filter.maaleresultaterOppstroems}
                        normalizeNumbers={true}
                        type="text"
                        onBlur={(e) => updateMaaleresultater(Number(e.target.value), i, "maaleresultaterOppstroems")}
                      />
                    </td>
                    <td>
                      <div
                        className="d-flex flex-row justify-content-around align-items-center"
                        style={{ backgroundColor: "#ebecec", borderRadius: "6px", height: "2.2rem" }}
                      >
                        <Radio
                          checked={filter.godkjent === true}
                          onChange={() => updateMaaleresultater(true, i, "godkjent")}
                          label="Ja"
                        />
                        <Radio
                          checked={filter.godkjent === false}
                          onChange={() => updateMaaleresultater(false, i, "godkjent")}
                          label="Nei"
                        />
                      </div>
                    </td>
                    <td>
                      <McInputNew
                        value={filter.konsentrasjonFoerFilterEtterTest}
                        normalizeNumbers={true}
                        type="text"
                        onBlur={(e) =>
                          updateMaaleresultater(Number(e.target.value), i, "konsentrasjonFoerFilterEtterTest")
                        }
                      />
                    </td>
                    {filter.godkjent === true && (
                      <>
                        <td>
                          <McInputNew
                            value={filter.maaleresultaterNedstroems}
                            normalizeNumbers={true}
                            type="text"
                            onBlur={(e) =>
                              updateMaaleresultater(Number(e.target.value), i, "maaleresultaterNedstroems")
                            }
                          />
                        </td>
                        <td>
                          <McInputNew
                            value={filter.beskrivelseAvLekkasjeLokasjon}
                            type="text"
                            onChange={(e) => updateMaaleresultater(e.target.value, i, "beskrivelseAvLekkasjeLokasjon")}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              ))}
            </table>
          </div>
        )}
        {Number(data.inndata.maaleresultater.length) > 0 && (
          <div className="mt-4">
            <ReportDocxImageContainer docxImageTag="lekkasjeskanningAvHepa" title="Målepunkter" />
            <div className="w-50">
              <McTextAreaNew
                label="Kommentar:"
                value={data.inndata.kommentar}
                onChange={(e) =>
                  onUpdateEditData("testResultater.lekkasjeskanningAvHepa.inndata.kommentar", e.target.value)
                }
              />
            </div>
          </div>
        )}
      </Stack>
    </Paper>
  );
};
export default LekkasjeskanningAvHepa;
